import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { V2, usePageTitle } from '../v2/shared';

interface RegisterFormData { name: string; email: string; password: string; confirmPassword: string }

const Register: React.FC = () => {
  usePageTitle('Crear cuenta');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register, handleSubmit, watch, formState: { errors } } = useForm<RegisterFormData>();
  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email, password: data.password, name: data.name }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || (response.status === 429 ? 'Demasiados intentos. Probá más tarde.' : 'No pudimos crear tu cuenta.'));
      }
      // Users must confirm their email before they can sign in.
      toast.success('¡Cuenta creada! Revisá tu email para confirmarla.', { duration: 6000 });
      navigate('/login');
    } catch (error: any) {
      toast.error(error.message || 'No pudimos crear tu cuenta.');
    } finally {
      setLoading(false);
    }
  };

  const Err = ({ k }: { k: keyof RegisterFormData }) => errors[k] ? <p className="err" role="alert">{errors[k]!.message}</p> : null;

  return (
    <V2>
      <main className="auth">
        <div className="auth-card">
          <div className="head">
            <h1>Creá tu cuenta de paciente</h1>
            <p>Para ver y gestionar tus turnos. No hace falta para pedir uno.</p>
          </div>
          <form className="panel" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="field">
              <label htmlFor="name">Nombre y apellido</label>
              <input id="name" autoComplete="name" className={`in ${errors.name ? 'bad' : ''}`}
                {...register('name', { required: 'Ingresá tu nombre', minLength: { value: 2, message: 'Mínimo 2 letras' } })} />
              <Err k="name" />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" autoComplete="email" inputMode="email" className={`in ${errors.email ? 'bad' : ''}`}
                {...register('email', { required: 'Ingresá tu email', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Revisá el formato del email' } })} />
              <Err k="email" />
            </div>
            <div className="field">
              <label htmlFor="password">Contraseña <span className="opt">(mínimo 8 caracteres)</span></label>
              <div className="pw">
                <input id="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className={`in ${errors.password ? 'bad' : ''}`}
                  {...register('password', { required: 'Elegí una contraseña', minLength: { value: 8, message: 'Mínimo 8 caracteres' } })} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <Err k="password" />
            </div>
            <div className="field">
              <label htmlFor="confirmPassword">Repetí la contraseña</label>
              <input id="confirmPassword" type={showPassword ? 'text' : 'password'} autoComplete="new-password" className={`in ${errors.confirmPassword ? 'bad' : ''}`}
                {...register('confirmPassword', { required: 'Repetí la contraseña', validate: v => v === password || 'Las contraseñas no coinciden' })} />
              <Err k="confirmPassword" />
            </div>
            <button type="submit" className="btn2 btn2-cta" style={{ width: '100%' }} disabled={loading}>
              {loading ? <><Loader2 size={18} className="animate-spin" /> Creando cuenta…</> : 'Crear cuenta'}
            </button>
          </form>
          <p className="auth-alt">¿Ya tenés cuenta? <Link to="/login">Ingresá</Link></p>
        </div>
      </main>
    </V2>
  );
};

export default Register;
